import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef, Children, isValidElement, cloneElement } from "react";

/* -------------------------------------------------------------------------- */
/*  MaskedLines — line-by-line editorial reveal for hero headlines.           */
/*  Each child (or newline-split string) becomes its own clipped row that     */
/*  slides in from below with a serif-friendly cubic curve.                   */
/* -------------------------------------------------------------------------- */
const LINE_EASE = [0.22, 0.9, 0.24, 1];

export function MaskedLines({ children, delay = 0, stagger = 0.09, as = "span", className = "" }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.span;

  const items = Array.isArray(children) ? children : [children];
  const lines = items.flatMap((c, i) => {
    if (typeof c === "string") {
      return c.split("\n").map((t, j) => (
        <span key={`${i}-${j}`}>{t}</span>
      ));
    }
    if (isValidElement(c)) return [cloneElement(c, { key: c.key ?? i })];
    return [];
  });

  return (
    <Tag className={className} aria-label={typeof children === "string" ? children : undefined}>
      {lines.map((node, idx) => (
        <span
          key={idx}
          className="block overflow-hidden"
          style={{ WebkitMaskImage: "linear-gradient(black, black)" }}
        >
          <motion.span
            className="block will-change-transform"
            initial={reduce ? false : { y: "115%", opacity: 0 }}
            animate={reduce ? { y: 0, opacity: 1 } : { y: "0%", opacity: 1 }}
            transition={{
              duration: 1.05,
              ease: LINE_EASE,
              delay: delay + idx * stagger,
            }}
          >
            {node}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/*  Reveal — scroll-triggered fade & rise, only once, respects prefers-       */
/*  reduced-motion.                                                           */
/* -------------------------------------------------------------------------- */
export function Reveal({
  children,
  as = "div",
  delay = 0,
  y = 28,
  duration = 0.9,
  className = "",
  once = true,
  amount = 0.25,
  ...rest
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, amount });
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;

  return (
    <Tag
      ref={ref}
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration, ease: LINE_EASE, delay }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* -------------------------------------------------------------------------- */
/*  StaggerGroup + StaggerItem — for grid/list scroll reveals.                */
/* -------------------------------------------------------------------------- */
export function StaggerGroup({ children, className = "", stagger = 0.08, amount = 0.2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount });
  const reduce = useReducedMotion();

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView || reduce ? "show" : "hidden"}
      variants={{ hidden: {}, show: { transition: { staggerChildren: stagger } } }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, y = 24, className = "", ...rest }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: LINE_EASE } },
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Marquee — slow editorial ticker (drop-in, uses framer-motion).            */
/*  Renders `items` twice so the loop is seamless.                             */
/* -------------------------------------------------------------------------- */
export function Marquee({ items, duration = 55, className = "", separator = "◆" }) {
  const list = [...items, ...items];
  return (
    <div className={`overflow-hidden whitespace-nowrap ${className}`}>
      <motion.div
        className="inline-flex items-center gap-14 will-change-transform"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration, ease: "linear", repeat: Infinity }}
      >
        {list.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-14 shrink-0">
            <span className="font-display italic text-inherit">{it}</span>
            <span aria-hidden className="opacity-40 text-sm">{separator}</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}
